import axios, { AxiosError } from 'axios';
import i18n from 'i18next';

import { isChunkLoadError } from '@/shared/lib/chunkLoadError';
import { translateUploadError } from '@/shared/lib/uploadErrors';

export const getBackendErrorMessage = (error: AxiosError<any>): string => {
  if (error.response) {
    if (error?.response?.data?.violations) return error.response.data.violations[0]?.message;
    const message: string =
      error.response.data.detail ||
      error.response.data.errorMessage ||
      error.response.data.error_message ||
      error.response.data.message ||
      error.message;
    const uploadError =
      typeof message === 'string' ? translateUploadError(message, i18n.t) : undefined;
    if (uploadError) return uploadError;
    // A proxy rejects an oversized body before the backend sees it, with an HTML 413 and no code.
    if (error.response.status === 413) return i18n.t('upload_errors.UPLOAD_TOO_LARGE_NO_LIMIT');
    return message;
  }

  return error.message;
};

export type ErrorKind =
  | 'notFound'
  | 'forbidden'
  | 'unauthorized'
  | 'network'
  | 'server'
  | 'chunk'
  | 'unknown';

const NETWORK_TYPE_ERROR_PATTERN =
  /failed to fetch|networkerror|load failed|network request failed/i;

const statusToErrorKind = (status: number): ErrorKind => {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status >= 500) return 'server';
  return 'unknown';
};

const hasNumericStatus = (error: object): error is { status: number } =>
  'status' in error && typeof (error as { status: unknown }).status === 'number';

export function getErrorKind(error: unknown): ErrorKind {
  if (isChunkLoadError(error)) return 'chunk';

  if (axios.isAxiosError(error)) {
    if (error.code === 'ERR_CANCELED') return 'unknown';
    if (!error.response) return 'network';
    return statusToErrorKind(error.response.status);
  }

  if (typeof error === 'object' && error !== null && hasNumericStatus(error)) {
    return statusToErrorKind(error.status);
  }

  if (error instanceof TypeError && NETWORK_TYPE_ERROR_PATTERN.test(error.message)) {
    return 'network';
  }

  return 'unknown';
}

/** One snackbar for "can't reach the server", however many requests failed at the same time. */
export const NETWORK_ERROR_SNACKBAR_KEY = 'network-error';

/**
 * Finds which of `codes` a backend refusal carries. Some lanes append context after the code
 * (`SELLING_EDIT_REQUIRES_EXTEND: <bookingId>`), so the code may sit anywhere in the message.
 */
export const findBackendErrorCode = <Code extends string>(
  error: unknown,
  codes: readonly Code[],
): { code?: Code; raw: string } => {
  const raw = getBackendErrorMessage(error as AxiosError) ?? '';

  return { code: codes.find((candidate) => raw.includes(candidate)), raw };
};
